import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Copy, Edit2, Plus, Trash2 } from 'lucide-react';

import { InputFormatDialog } from '../../components/admin/input-format/InputFormatDialog';
import {
  useDeleteInputFormat,
  useDuplicateInputFormat,
  useInputFormats,
} from '../../services/llm-input-format.service';
import { useLLMProviders } from '../../services/llm-catalog.service';
import { InputFormatListItem } from '../../types/admin';

export const InputFormatsManagement: React.FC = () => {
  const { data: formats = [], isLoading } = useInputFormats();
  const { data: providers = [] } = useLLMProviders();
  const duplicateFormat = useDuplicateInputFormat();
  const deleteFormat = useDeleteInputFormat();
  const [editing, setEditing] = React.useState<InputFormatListItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [duplicating, setDuplicating] = React.useState<InputFormatListItem | null>(null);
  const [duplicateCode, setDuplicateCode] = React.useState('');
  const [duplicateName, setDuplicateName] = React.useState('');
  const [deleting, setDeleting] = React.useState<InputFormatListItem | null>(null);

  const openCreate = () => {
    setEditing(null);
    setIsDialogOpen(true);
  };

  const openEdit = (format: InputFormatListItem) => {
    setEditing(format);
    setIsDialogOpen(true);
  };

  const openDuplicate = (format: InputFormatListItem) => {
    setDuplicating(format);
    setDuplicateCode(`${format.code}-copy`);
    setDuplicateName(`${format.name} (copy)`);
  };

  const confirmDuplicate = async () => {
    if (!duplicating) return;
    await duplicateFormat.mutateAsync({
      publicId: duplicating.publicId,
      code: duplicateCode.trim(),
      name: duplicateName.trim(),
    });
    setDuplicating(null);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteFormat.mutateAsync(deleting.publicId);
      setDeleting(null);
    } catch {
      // The mutation already shows the server message (e.g. 409 while models use the format).
    }
  };

  return (
    <Box className="p-6 text-gray-900 dark:text-slate-100">
      <Box className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold">
            Input Formats
          </Typography>
          <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
            Shared, named input and response formats (e.g. &quot;Google text to video&quot;). A
            model picks one in the LLM Catalog and adds its own fields there.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={openCreate}>
          New Format
        </Button>
      </Box>

      <Paper className="overflow-hidden dark:bg-slate-800">
        {isLoading ? (
          <Box className="flex justify-center p-8">
            <CircularProgress />
          </Box>
        ) : formats.length === 0 ? (
          <Alert severity="info" className="m-4">
            No input formats yet. Create one, then select it in a model of the LLM Catalog.
          </Alert>
        ) : (
          <Box className="overflow-x-auto">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell align="right">In</TableCell>
                  <TableCell align="right">Out</TableCell>
                  <TableCell align="right">Models</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formats.map((format) => (
                  <TableRow key={format.publicId} hover>
                    <TableCell>
                      <Typography variant="body2" className="font-medium">
                        {format.name}
                      </Typography>
                      {format.description && (
                        <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                          {format.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">{format.code}</TableCell>
                    <TableCell>
                      {format.providerName ? (
                        <Chip size="small" label={format.providerName} />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">{format.inFieldCount}</TableCell>
                    <TableCell align="right">{format.outFieldCount}</TableCell>
                    <TableCell align="right">{format.modelCount}</TableCell>
                    <TableCell>v{format.version}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={format.isActive ? 'success' : 'default'}
                        label={format.isActive ? 'Active' : 'Inactive'}
                      />
                    </TableCell>
                    <TableCell align="right" className="whitespace-nowrap">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(format)}>
                          <Edit2 size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton size="small" onClick={() => openDuplicate(format)}>
                          <Copy size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleting(format)}>
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <InputFormatDialog
        open={isDialogOpen}
        format={editing}
        providers={providers}
        onClose={() => setIsDialogOpen(false)}
      />

      <Dialog open={!!duplicating} onClose={() => setDuplicating(null)} fullWidth maxWidth="xs">
        <DialogTitle>Duplicate format</DialogTitle>
        <DialogContent className="space-y-3">
          <TextField
            className="mt-2"
            fullWidth
            label="Name"
            value={duplicateName}
            onChange={(event) => setDuplicateName(event.target.value)}
          />
          <TextField
            fullWidth
            label="Code"
            value={duplicateCode}
            onChange={(event) => setDuplicateCode(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicating(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!duplicateCode.trim() || !duplicateName.trim() || duplicateFormat.isPending}
            onClick={confirmDuplicate}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete format?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete &quot;{deleting?.name}&quot;? A format that models still use cannot be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" disabled={deleteFormat.isPending} onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
